// ════════════════════════════════════════════════════════════
//  online/stage.js — THE STAGE: one fight at a time, back to back, all day
// ════════════════════════════════════════════════════════════
//  caP'n's rule: "always a guaranteed fight to watch" — and the League as a
//  real sport (circuit.js). One stage — the Circuit first, the Contenders as filler:
//    ringside → bell → fight (≤ 5:00) → ringside → bell → …
//
//  ── THE HOUR ────────────────────────────────────────────────
//   :50 (of the hour BEFORE)  the card is LOCKED and bets open — ten minutes to look
//           and bet (caP'n, 2026-10-17: "that 10 minutes before the fight is
//           absolutely crucial for betting"). It knows every result before the
//           last hour + the last hour's fights that had ended by :50 (circuit.js
//           isKnown); the fight still on air then is the last one, and it only
//           counts as "fought", not "won". Needs the hour before on the stage
//           (planOf's opts.prev) — a screen without it waits for :00:03, the old
//           way, as does the first hour of a week (its leagues need last week's
//           last result) and any hour whose record isn't in yet.
//   :00:03  …the fallback: locked only once every result before it is in —
//           ledger.js, ORDER IS THE LAW. The Mind picks everyone's
//           playbook right at the lock (lock.pb), so the League keeps learning.
//   bout 0  rings RING_OPEN after the HOUR starts (:00:03 + RING_OPEN); each next bell = the bout before
//           it ENDS + its ringside. No bell after :53 — the rest of the card
//           is scratched (bouts aren't fought, nothing is recorded).
//
//  ── HOW A SCREEN KNOWS WHEN A FIGHT ENDS ────────────────────
//   Every League fight is NPC only, so seed + teams decide it completely:
//   once a bout's seed exists (written AT its bell), any screen can replay it
//   headless (~0.2 s) and know its end — and so the next bell. The result is
//   only SHOWN (and written) once the fight has actually run its time on
//   air; a board never spoils a fight that's still on.
//
//  ── NOBODY IN CHARGE ────────────────────────────────────────
//   · A bout's spec (who, its bell, no seed) is written once its bell is known.
//   · Its SEED is added at the bell, by whichever screen gets there — nobody
//     can know a fight before the bell, so nobody bets on a sure thing.
//   · Bets on any locked bout are open until its bell (bets refund if late).
//
//  games/shatterproof/league/hours/{h}: lock { card, pb, note, kind, paper? }
//  games/shatterproof/matches/bt_{h}_{j}: a normal match (spec, result, bets_<pairing>)
// ════════════════════════════════════════════════════════════
import { BASE } from "./ie.js";
import { matchResult, replayMatch } from "./lockstep.js";
import {
  BEINGS, CALL, WORLD, LOCK_GRACE_MS, isChKey, EST_FIGHT_S, hourInfo, hourOf, hourStart, planHour, ringOf, lastBellOf, paperBell, seasonOf, seasonWeeks, KINDS, lockAtOf, knownRows, needsSeasonRows,
  laneOf, paperAt, callOf, closersFit, closerAfter, isCloser, isBound, resolveBound, boutKey, feedersOf, finalR, BREATHER_S,
} from "./circuit.js";
import { rowOf } from "./rows.js";
import { seatClaims, guestEntrant } from "./guests.js";
import { leagueCtx, readyFor, challengerEntrant, memberData } from "./challengers.js";
import { ledgerOf, leagueOf, cursorOf, hourPath } from "./ledger.js";
import { foldMinds, decideHour } from "./mind.js";
import { TEAM_SIZE } from "../sim/battle.js";

export { hourOf, hourStart, KINDS };
export const LOCK_GRACE = LOCK_GRACE_MS;
export const boutId = (h, j) => `bt_${h}_${j}`;
export const boutPath = (h, j) => `${BASE}/matches/${boutId(h, j)}`;
/** bt_{h}_{j} → { h, j } (null for anything else) */
export function parseBoutId(id) {
  const m = /^bt_(-?\d+)_(\d+)$/.exec(String(id));
  return m ? { h: Number(m[1]), j: Number(m[2]) } : null;
}
const TICK_HZ = 120;

// per net (each test world has its own): finished fights, known ends, locked plans
const memo = (store) => (net) => { let m = store.get(net); if (!m) { m = new Map(); store.set(net, m); } return m; };
export const doneOf = memo(new WeakMap());     // path → a finished fight's result (never changes)
const endsOf = memo(new WeakMap());             // path → { tick, res } from a replay (an NPC fight is decided at its seed)
const lockedOf = memo(new WeakMap());           // h → a locked plan
const voidedOf = memo(new WeakMap());           // path → true once its VOID is written (a scratched bout)
/** Write a scratched bout down as VOID (once), so bets on it refund. Resolves the spec that's stored. */
async function scratch(net, path, b, at) {
  if (voidedOf(net).has(path)) return { void: true, scratched: true };
  const v = (await net.txn(`${path}/spec`, (cur) => (cur ? undefined : { void: true, scratched: true, keys: [b.a, b.b].filter((x) => typeof x === "string"), createdAt: at }))).value;
  if (!v || v.void) voidedOf(net).set(path, true);
  return v || { void: true, scratched: true };
}

export function finish(f, res, net) {
  Object.assign(f, { winner: res.winner, why: res.why, by: res.call || res.by || null, up: res.up || null, an: res.an || null, phase: "done" });
  if (f.endAt == null) f.endAt = res.endAt != null ? res.endAt : f.startAt + Math.round((res.tick || 0) * 1000 / TICK_HZ);
  if (net) doneOf(net).set(f.path, { winner: f.winner, why: f.why, by: f.by, up: f.up, an: f.an, endAt: f.endAt });
}
export const newSeed = (now) => ((Math.floor(Math.random() * 2 ** 31) ^ (now & 0x7fffffff)) >>> 0) || 7;

// ── WHO A KEY IS ─────────────────────────────────────────────
// npc… = the Circuit's cast · c… = the Contenders' · ch_{uid} = a CHALLENGER (a player or partner; ch = lock.ch, who they were
// and what they fought with when the card went up). pb = the hour's playbooks (lock.pb, mind.js): how NPCs fight this hour.
export function entrantOf(key, pb = null, ch = null) {
  if (isChKey(key)) return challengerEntrant(key, ch && ch[key]);
  const being = BEINGS.get(key);
  if (!being) return null;
  const p = pb && pb[key];
  const team = p && Array.isArray(p.t) && p.t.length === TEAM_SIZE ? p.t : being.team;
  return { key, name: being.name, team, home: being.home, style: (p && p.s) || null, change: (p && p.c) || null };
}
/** The minds going into hour h: the week's start + every row before h (ORDER IS THE LAW). */
export const mindsAt = (league, rows, h) => foldMinds(league.minds, knownRows(rows, h));   // what the card knows (circuit.js isKnown)

// (a LATE-BOUND bout — circuit.js isBound — names a side by the bout that feeds it: fa / fb instead of a / b)
const sideOk = (b, s, f) => typeof b[s] === "string" || (b.k === "cup" && typeof b[f] === "string");
const validCard = (card) => Array.isArray(card) && card.every((b) => b && typeof b.k === "string" && sideOk(b, "a", "fa") && sideOk(b, "b", "fb"));
/**
 * Hour h's card. While the record behind it is still coming in it's PROVISIONAL; from :50 of the hour
 * before (when opts.prev — that hour, as loadHour resolved it — is handed in and the record up to the
 * hour before it is in) or from :00:03 (once every result before it is in), it's LOCKED and never changes.
 * Resolves { info, card, pb, note, locked, paper, pending } (pending = "catching up").
 * opts.paper (keeper.js): decided on paper — the hour node is handed in (opts.node), and the
 * lock's write isn't waited on (opts.pending gets it).
 * opts.prev (loadStage): hour h−1 on the stage — its finished fights stand in for ledger rows the
 * keeper only writes when the whole hour is over, and its unfinished ones become the card's "tail".
 */
export async function planOf(net, h, now, opts = {}) {
  const info = hourInfo(h);
  if (lockedOf(net).has(h)) return lockedOf(net).get(h);
  const paper = !!opts.paper;
  const node = (paper && opts.node !== undefined ? opts.node : await net.get(hourPath(h))) || {};
  if (node.lock && validCard(node.lock.card)) {
    const p = { info, card: node.lock.card, pb: node.lock.pb || null, ch: node.lock.ch || null, note: node.lock.note || "", locked: true, paper: !!node.lock.paper, seats: Array.isArray(node.lock.seats) ? node.lock.seats : [] };
    lockedOf(net).set(h, p);
    return p;
  }
  // the cursor FIRST, then the rows: rows read after it hold at least everything it promises
  const cursor = paper ? Infinity : await cursorOf(net);
  const prev = !paper && opts.prev && opts.prev.hour === h - 1 && opts.prev.locked ? opts.prev : null;
  const lateReady = paper || cursor >= h - 1;                                  // every result before h is in: the old way, at :00:03
  const earlyReady = !paper && !!prev && cursor >= h - 2 && now >= lockAtOf(h);  // everything before the last hour is in, and the last hour is on the stage: :50
  const ready = lateReady || earlyReady;
  const stored = await leagueOf(net, info.week);
  if (!stored) return { info, card: null, pb: null, note: "", locked: false, pending: true };
  const lockNow = (lateReady && now >= hourStart(h) + LOCK_GRACE) || earlyReady;
  // CHALLENGERS (challengers.js leagueCtx): who has joined (in the Contenders from the card after they join) and the week's cup
  // check-ins — read the same way by every planner (live, paper, the week turn, the boards). "I'm in" is live-only: a paper hour
  // has nobody there to fight. A lock is forever, so while locking a failed read means NOT NOW (never "nobody").
  let ctxL, present = new Set();
  const chData = new Map();
  try {
    ctxL = await leagueCtx(net, h, stored, now, { strict: lockNow });
    if (!paper && ctxL.members.size) {
      const raw = lockNow ? await net.get(`${BASE}/league/ready`) : await net.get(`${BASE}/league/ready`).catch(() => null);
      const R = readyFor(raw || {}, ctxL.members, h);
      present = new Set(R.keys());
      for (const [k, d] of R) chData.set(k, d);
    }
  } catch (e) {
    console.warn("[stage] couldn't read the League's Challengers — the card waits", e); // eslint-disable-line no-console
    return { info, card: null, pb: null, note: "", locked: false, pending: true };
  }
  const league = ctxL.league, entered = ctxL.entered, cupData = (c, k) => ctxL.cupData(c, info.day, k);
  // a lock is forever, so it's built from the SERVER's rows, read now — never a cache that may lag
  let rows = lockNow && !paper ? await ledgerOf(net).fresh(info.week) : await ledgerOf(net).rows(info.week);
  // the hour on air hasn't reached the ledger (the keeper writes it when the hour is over): its finished fights count from the stage
  let tail = [];
  if (prev) {
    if (prev.info.counts && prev.info.week === info.week) {
      const have = new Set(rows.map((r) => r.id)), extra = [];
      for (const f of prev.bouts) { const r = rowOf(f, prev.info); if (r && !have.has(r.id)) extra.push(r); }
      if (extra.length) rows = rows.concat(extra);
    }
    tail = prev.bouts.filter((f) => f.A && f.B && f.phase !== "done" && f.phase !== "scratched").map((f) => ({ a: f.A.key, b: f.B.key, ln: laneOf(f) }));
  }
  // the Season Finale seeds by the whole season: the weeks before this one (complete — the cursor passed them) + this one
  const seasonRows = needsSeasonRows(info)
    ? (await Promise.all(seasonWeeks(seasonOf(info.week)).filter((w) => w < info.week).map((w) => (lockNow && !paper ? ledgerOf(net).fresh(w) : ledgerOf(net).rows(w))))).flat().concat(rows)
    : null;
  const plan = planHour(h, { league, weekRows: rows, seasonRows, tail, present, entered });
  const ch = {};
  // what each Challenger fights with on this card: a cup bout → the three they checked in with; a league bout → their "I'm in" three
  for (const b of plan.card) for (const k of [b.a, b.b]) {
    if (!isChKey(k)) continue;
    // …and a bout they can't skip (Apocalypse — no "I'm in" needed for a seat on the line) with what they LAST fought with:
    // their partner fights it or it's a forfeit, but it's always fought, so a series never stalls
    const d = (b.k === "cup" ? cupData(b.c, k) : chData.get(k)) || memberData(ctxL.members.get(k));
    if (d) ch[k] = d;
  }
  // THE MIND: everyone on the card picks their playbook from everything before this hour
  const pb = decideHour(mindsAt(league, rows, h), plan.card.flatMap((b) => [b.a, b.b]).filter((k) => typeof k === "string"), h);
  if (lockNow) {
    // (GUESTS' seats — 2026-10-20 — are no longer written: Challengers are on the card as themselves. Old locks' seats still read.)
    const lock = { card: plan.card, pb, note: plan.note, kind: info.kind, ...(paper ? { paper: true } : {}), ...(Object.keys(ch).length ? { ch } : {}) };
    let v = lock;
    // paper: not waited on — and if it fails, forget the plan so the next pass writes the lock again
    if (paper) (opts.pending || []).push(net.txn(`${hourPath(h)}/lock`, (cur) => (cur && cur.card ? undefined : lock)).catch((e) => { lockedOf(net).delete(h); throw e; }));
    else v = (await net.txn(`${hourPath(h)}/lock`, (cur) => (cur && cur.card ? undefined : lock))).value || lock;
    const p = { info, card: validCard(v.card) ? v.card : plan.card, pb: v.pb || null, ch: v.ch || null, note: v.note || plan.note, locked: true, paper: !!v.paper, seats: Array.isArray(v.seats) ? v.seats : [] };
    lockedOf(net).set(h, p);
    return p;
  }
  return { info, card: plan.card, pb, ch, note: plan.note, locked: false, pending: !ready && now >= hourStart(h) };
}

/** A bout's match spec. */
export function boutSpec({ h, j, bout, A, B, startAt }) {
  // a GUEST side is a human (their own screen plays it); never showing up → their partner (the AI) or a forfeit
  const humans = {}, noShow = {};
  [A, B].forEach((E, s) => { if (E.uid) { humans[s] = E.uid; noShow[s] = E.partner ? "ai" : "forfeit"; } });
  return {
    teams: [A.team, B.team], tomes: [A.tomes || null, B.tomes || null], names: [A.name, B.name], humans, ...(Object.keys(noShow).length ? { noShow } : {}),
    styles: [A.style || null, B.style || null], keys: [A.key, B.key],
    kind: "bt", bout: { k: bout.k, ...(bout.dv ? { dv: bout.dv } : {}), ...(bout.fx ? { fx: bout.fx } : {}), ...(bout.c ? { c: bout.c } : {}), ...(bout.r != null ? { r: bout.r } : {}), ...(bout.i != null ? { i: bout.i } : {}), ...(bout.g != null ? { g: bout.g } : {}), ...(bout.ln ? { ln: bout.ln } : {}) },
    hk: hourInfo(h).kind, hour: h, index: j, server: WORLD, callTime: callOf(bout), startAt, createdAt: startAt,
  };
}
/** Write the spec once its bell is known, and the seed once the bell has rung (each exactly once). */
export async function ensureSpec(net, path, base, now) {
  let spec = await net.get(`${path}/spec`);
  if (!spec) spec = (await net.txn(`${path}/spec`, (cur) => (cur ? undefined : base))).value || base;
  // never on a guessed clock: until the server has told us its time, a fast device could seed early
  if (spec.seed == null && now >= spec.startAt && (!net.clockKnown || net.clockKnown())) {
    const seed = newSeed(now);
    // ⚠ Firebase runs a transaction FIRST on its local copy, which is often empty (null) even when
    // the spec exists: answer null with the spec we read, and the server re-runs it on the real one
    spec = (await net.txn(`${path}/spec`, (cur) => (cur == null ? { ...spec, seed } : cur.seed == null ? { ...cur, seed } : undefined))).value || spec;
  }
  return spec;
}
/**
 * A watcher sent to a fight just before its bell: wait for the seed (and at the bell write it, if
 * nobody has). Resolves the seeded spec, or null.
 */
export async function awaitSeed(net, path, now, { until = null, graceMs = 25000 } = {}) {
  for (;;) {
    const spec = await net.get(`${path}/spec`);
    if (spec && spec.void) return null;
    if (spec && spec.seed != null) return spec;
    if (spec && now() >= spec.startAt) { const s2 = await ensureSpec(net, path, spec, now()); if (s2 && s2.seed != null) return s2; }
    const deadline = (spec ? spec.startAt : until || 0) + graceMs;
    if (now() > deadline) return null;
    await new Promise((r) => setTimeout(r, 500));
  }
}
/**
 * How a seeded bout ends: the stored result if a screen saw it, else a headless replay (an NPC
 * fight is decided by its seed). Remembered, so it's replayed once per screen at most.
 * Resolves { tick, res } or null (no seed yet / can't be replayed).
 */
async function endOf(net, path, spec, now) {
  const cache = endsOf(net);
  if (cache.has(path)) return cache.get(path);
  if (!spec || spec.seed == null) return null;
  let res = await net.get(`${path}/result`);
  // A FIGHT WITH A PLAYER IN IT (a guest) is decided by the player, not predicted: only their own result counts
  // until the fight is certainly over (the call + 20 s, as matchResult does) — then a replay of what they sent
  // (and the AI / forfeit for a no-show, by the record) settles it.
  const human = !!(spec.humans && (spec.humans[0] || spec.humans[1]));
  if (!res && human && now < spec.startAt + ((spec.callTime || CALL) + 20) * 1000) return null;
  // the replay runs to the call, whatever the wall clock says (the driver ticks up to the "now" it's given)
  if (!res) res = await replayMatch(net, path, spec.startAt + ((spec.callTime || CALL) + 30) * 1000);
  if (!res || !Number.isFinite(res.tick)) return null;
  const v = { tick: res.tick, res };
  cache.set(path, v);
  return v;
}

/**
 * Where an hour stands. Writes what's due (lock, specs, seeds, results) and reads what's known.
 * Resolves { hour, start, info, note, locked, pending, paper, pb, bouts:[f] }
 * f = { id, path, h, j, k, dv, fx, c, r, i, g, A, B, prepAt, startAt (the bell, null until known), endAt,
 *       winner, why, by, up, an, phase }
 * phase: "waiting" (names not settled) · "soon" (on the card, bell not yet rung) · "prep" (ringside: bell
 *        known, bets close at it) · "live" · "done" · "scratched" (didn't fit before :53)
 */
export async function loadHour(net, { hour, now, prev = null }) {
  const h = hour, plan = await planOf(net, h, now, { prev });
  const base = { server: WORLD, hour: h, start: hourStart(h), info: plan.info, note: plan.note, locked: plan.locked, pending: !!plan.pending, paper: !!plan.paper, pb: plan.pb || null };
  const card = plan.card || [];
  const bouts = [];
  // GUESTS: which bouts have a seat, and (once the seats close) who took it
  const seatAt = new Map();   // j → [{ side, i, guest|null, open }]
  if (plan.locked && !plan.paper && Array.isArray(plan.seats) && plan.seats.length) {
    const cl = await seatClaims(net, h, now);
    plan.seats.forEach((s, i) => {
      const g = cl.byIndex[i];
      const v = { side: s.side, i, guest: cl.final && g ? g : null, open: !cl.final, taken: !!g };
      seatAt.set(s.j, [...(seatAt.get(s.j) || []), v]);
    });
  }
  // ONE CHAIN PER STAGE (V3 runs the Circuit stage and the Contenders stage side by side; before it, everything is stage 0):
  // prevEnd = when that stage's last fight ended · chain = that stage's next bell is known · cut = that stage passed :53
  const prevEnds = [hourStart(h) + LOCK_GRACE, hourStart(h) + LOCK_GRACE], chains = [plan.locked && !plan.paper, plan.locked && !plan.paper], cuts = [false, false];
  // LATE-BOUND BOUTS (circuit.js isBound): a Qualifier's later rounds roll in this very hour — each side is read off the bout that feeds it, the moment that
  // bout has ended. won = "c:r:i" → the key that won it; gone = the cup bouts that were scratched (what waits on them never rings).
  const won = new Map(), gone = new Set(), rest = [false, false];   // rest: a stage whose last RUNG bout was a tournament's final waits BREATHER_S before its next bell (circuit.js ringOf — even when a scratched bout sits between)
  for (let j = 0; j < card.length; j++) {
    const b = card[j], path = boutPath(h, j), ln = laneOf(b);
    let prevEnd = prevEnds[ln], chain = chains[ln], cut = cuts[ln], fNow = null;
    const keep = () => {
      prevEnds[ln] = prevEnd; chains[ln] = chain; cuts[ln] = cut;
      if (fNow && fNow.startAt != null && fNow.phase !== "scratched") rest[ln] = fNow.k === "cup" && fNow.r === finalR(fNow.c);
      if (fNow && fNow.k === "cup") {
        if (fNow.phase === "done" && !(fNow.winner === 0 || fNow.winner === 1)) gone.add(boutKey(fNow.c, fNow.r, fNow.i));   // ended with no winner: whatever it feeds can't be named
        else if (fNow.phase === "done" && fNow.A && fNow.B) won.set(boutKey(fNow.c, fNow.r, fNow.i), fNow.winner === 0 ? fNow.A.key : fNow.B.key);
        else if (fNow.phase === "scratched") gone.add(boutKey(fNow.c, fNow.r, fNow.i));
      }
    };
    try {   // (every way out of this bout — each `continue` too — hands the stage's chain back: finally)
    const sides = isBound(b) ? resolveBound(b, won) : null;
    let A = entrantOf(sides ? sides.a : b.a, plan.pb, plan.ch), B = entrantOf(sides ? sides.b : b.b, plan.pb, plan.ch);
    const st = seatAt.get(j) || [];
    for (const v of st) if (v.guest) { if (v.side === 0) A = guestEntrant(v.guest); else B = guestEntrant(v.guest); }
    const f = { id: boutId(h, j), path, h, j, ...b, ...(sides ? { a: sides.a, b: sides.b } : {}), A, B, prepAt: null, startAt: null, endAt: null, winner: null, why: null, by: null, up: null, an: null, phase: "waiting" };
    fNow = f;
    if (st.length) f.seats = st.map((v) => ({ side: v.side, i: v.i, open: v.open, taken: v.taken, uid: v.guest ? v.guest.uid : null }));
    if (st.some((v) => v.open)) f.open = true;   // a seat still open: who fights isn't settled, so no bets yet
    bouts.push(f);
    if (isBound(b) && !sides) {   // a side still waits on a bout of this hour: it can't be named (and can't ring) until that one ends
      const dead = cut || feedersOf(b).some((x) => x && gone.has(x));
      if (dead) { if (plan.locked && !plan.paper) await scratch(net, path, b, hourStart(h)).catch(() => {}); f.phase = "scratched"; }
      else { f.phase = plan.paper ? "waiting" : plan.locked ? "soon" : now < hourStart(h) ? "soon" : "waiting"; if (!plan.paper) chain = false; }
      continue;
    }
    if (!A || !B) { f.phase = "scratched"; continue; }
    if (cut) {   // after the cut: void too, so a bet placed on it early refunds
      if (plan.locked && !plan.paper) await scratch(net, path, b, hourStart(h)).catch(() => {});
      f.phase = "scratched"; continue;
    }
    if (plan.paper) {   // decided on paper: there is no match — its result is its ledger row
      const n = paperAt(card, j);   // each stage fights its own PAPER_BOUTS: its closers always, then the first fillers (circuit.js paperAt)
      if (n < 0) { f.phase = "scratched"; continue; }
      f.startAt = f.prepAt = paperBell(h, n);
      if (doneOf(net).has(path)) finish(f, doneOf(net).get(path)); else await fromRecord(net, f, plan.info);
      continue;
    }
    if (!plan.locked) { f.phase = now < hourStart(h) ? "soon" : "waiting"; continue; }
    if (!chain) { f.phase = "soon"; continue; }
    // no spoilers: the next bell (which says when the fight on air ends) isn't shown or written until it has ended
    if (now < prevEnd) { f.phase = "soon"; chain = false; continue; }
    let bell = prevEnd + Math.max(ringOf(card, j), rest[ln] ? BREATHER_S : 0) * 1000;
    f.prepAt = prevEnd;
    if (doneOf(net).has(path)) { f.startAt = bell; finish(f, doneOf(net).get(path)); prevEnd = f.endAt; continue; }
    if (bell > lastBellOf(h)) {
      // past :53 — scratched: written down as VOID (so any bets on it refund). A spec already there means
      // another screen rang this bell before the cut (its replay ran a hair shorter): the written bell wins.
      const v = await scratch(net, path, b, bell);
      if (v.void) { cut = true; f.phase = "scratched"; continue; }
    }
    // THE CLIMAX LAW (caP'n 2026-10-22): a filler is only fought if the stage's closers (its semifinals / final / best pair) still ring in time
    // even with every fight going to the 5:00 bell — else it alone is scratched (VOID, bets refund) and the stage goes on. The CLOCK scratches
    // fillers, never the closers. (A spec already written is another screen's call, made on the same record: the written bell wins.)
    if (!isCloser(b) && closerAfter(card, j) && !closersFit(card, j, bell, h)) {
      const v = await scratch(net, path, b, bell);
      if (v.void) { f.phase = "scratched"; continue; }
    }
    // GUESTS: a seat still open (the seats aren't frozen yet) — who fights isn't settled, so no spec yet
    if (f.open) { f.phase = now >= f.prepAt ? "prep" : "soon"; f.estAt = bell; chain = false; continue; }
    f.startAt = bell;
    let spec;
    try { spec = await ensureSpec(net, path, boutSpec({ h, j, bout: b, A, B, startAt: bell }), now); }
    catch (e) { console.warn("[stage] a bout that can't be set up is voided", f.id, e); const v = await scratch(net, path, b, bell).catch(() => null); f.phase = "scratched"; if (v && v.scratched) cut = true; continue; } // eslint-disable-line no-console
    if (spec && spec.void) { f.phase = "scratched"; if (spec.scratched) cut = true; continue; }   // a voided bout takes no time: the chain goes on
    // THE SPEC IS THE AUTHORITY on who fights (a screen that read the seats differently, an older build): follow it
    if (spec && Array.isArray(spec.keys)) {
      const fromSpec = (s, cur) => {
        if (!spec.keys[s] || spec.keys[s] === cur.key) return cur;
        if (spec.humans && spec.humans[s]) {
          const d = { uid: spec.humans[s], n: (spec.names || [])[s], t: (spec.teams || [])[s] || [], tm: (spec.tomes || [])[s] || null, p: spec.noShow && spec.noShow[s] === "ai" ? 1 : 0 };
          return isChKey(spec.keys[s]) ? challengerEntrant(spec.keys[s], d) || cur
            : guestEntrant({ uid: d.uid, name: d.n || "A guest", team: d.t, tomes: d.tm, partner: !!d.p });
        }
        return entrantOf(spec.keys[s], plan.pb, plan.ch) || cur;
      };
      f.A = A = fromSpec(0, A); f.B = B = fromSpec(1, B);
    }
    // the bell that was WRITTEN is the bell (a screen on another build may have worked it out a hair differently)
    if (spec && Number.isFinite(spec.startAt)) bell = f.startAt = spec.startAt;
    if (now < bell) { f.phase = now >= f.prepAt ? "prep" : "soon"; chain = false; continue; }
    let end = null;
    try { end = await endOf(net, path, spec, now); }
    catch (e) { console.warn("[stage] a replay failed", f.id, e); } // eslint-disable-line no-console
    if (!end) { f.phase = "live"; chain = false; continue; }   // rung, but the seed (or the replay) isn't in yet
    f.endAt = bell + Math.round(end.tick * 1000 / TICK_HZ);
    prevEnd = f.endAt;
    if (now < f.endAt) { f.phase = "live"; continue; }      // known to us, still on air: no spoilers
    // on air and over: the record (a screen that watched it wrote the same thing)
    const res = (await net.get(`${path}/result`)) || (await net.txn(`${path}/result`, (cur) => cur || { ...end.res, writer: "stage" })).value || end.res;
    finish(f, res, net);
    } finally { keep(); }
  }
  // ABOUT WHEN: a bell that isn't known yet (it would spoil the fight before it) is guessed from the bout before —
  // EST_FIGHT_S a fight — so the board can still say a time. f.estAt (+ f.est: a guess, never the first bell, which is fixed);
  // f.late: the guess falls past the last bell, so it may be scratched. Re-guessed from the real ends as fights finish.
  const ests = [hourStart(h) + LOCK_GRACE, hourStart(h) + LOCK_GRACE], seen = [0, 0];   // per stage
  for (let j = 0; j < bouts.length; j++) {
    const f = bouts[j], ln = laneOf(f);
    if (f.phase === "scratched" || ((!f.A || !f.B) && !isBound(f))) continue;   // (a late-bound bout still waiting on its feeder is guessed too: the bouts after it keep their place)
    const bell = f.startAt != null ? f.startAt : ests[ln] + ringOf(card, j) * 1000;
    // a filler the closers have no room for (at a normal fight's length) is "late": it may be scratched, and the stage goes on without it
    if (f.startAt == null && !isCloser(f) && closerAfter(card, j) && !closersFit(card, j, bell, h, (x) => Math.min(EST_FIGHT_S, callOf(x)))) { f.late = true; continue; }
    if (f.startAt == null) { if (bell > lastBellOf(h)) f.late = true; else { f.estAt = bell; f.est = plan.info.v3 ? seen[ln] > 0 : j > 0; } }   // (before V3 exactly as it was)
    seen[ln]++;
    ests[ln] = f.endAt != null ? f.endAt : bell + Math.min(EST_FIGHT_S, callOf(f)) * 1000;
  }
  return { ...base, bouts };
}

/** A fight decided on paper (keeper.js) has no match node — its result is its ledger row. */
export async function fromRecord(net, f, info) {
  const row = await ledgerOf(net).get(info.week, f.id);
  if (row) finish(f, { winner: row.w, why: row.why || "paper", endAt: row.at }, net);
  else f.phase = "waiting";   // the row is still on its way
}

/** The stage across the hour line, for the board: { prev, cur, next, on (live now), up (coming, in order) }. */
export async function loadStage(net, now) {
  const h = hourOf(now);
  const cur = await loadHour(net, { hour: h, now });
  const next = await loadHour(net, { hour: h + 1, now, prev: cur }).catch(() => null);   // prev: the next card can go up at :50
  const prev = await loadHour(net, { hour: h - 1, now }).catch(() => null);
  const live = (H, ln = null) => H && H.bouts.find((f) => f.phase === "live" && (ln == null || laneOf(f) === ln));
  const coming = (H) => (H ? H.bouts.filter((f) => f.A && f.B && (f.phase === "prep" || f.phase === "soon")) : []);
  // V3: two stages — onBy[ln] is what's on each; `on` stays "the fight on air" (the Circuit stage's, else the Contenders')
  const onBy = [live(cur, 0) || null, live(cur, 1) || null];
  const on = onBy[0] || onBy[1] || null;
  // the next hour's card only once it's locked (from :50: until then it may still change with this hour's results)
  // in BELL order (V3 has two stages: card order would list every Circuit-stage bout before the first Contenders-stage one)
  const bellAt = (f) => (f.startAt != null ? f.startAt : f.estAt != null ? f.estAt : Infinity);
  const up = [...coming(cur), ...(next && next.locked ? coming(next) : [])].map((f, n) => ({ f, n })).sort((x, y) => bellAt(x.f) - bellAt(y.f) || x.n - y.n).map((x) => x.f);
  return { prev, cur, next, on, onBy, up };
}

// matchResult stays the door bets use (rooms.js poolResultOf): for a League bout it finds the stored result
export { matchResult };
